import {
  faBan,
  faCheck,
  faXmark,
  faRefresh,
  faPenToSquare,
  faInfoCircle
} from '@fortawesome/pro-regular-svg-icons';

import { IPeerReviewWFState } from '../types/workflow-phases';
import { IPeerReviewWithCaseData } from '../models/review';

type PeerReviewCaseData = IPeerReviewWithCaseData['caseData'];

type PeerReviewCaseDataForRating = Pick<
  PeerReviewCaseData,
  'firstAssessment' | 'revisionAssessment' | 'revisionAssessmentHistory'
>;

/**
 * Workflow states where the current-round peer-review table must not offer
 * rate review. In-progress assessment is included: a recommendation from an
 * earlier round must not surface Rate Review while this round is still open.
 */
export const PEER_REVIEW_WORKFLOW_STATES_BLOCKING_EDITOR_RATE = [
  'invited',
  'revisionInvited',
  'firstAssessment',
  'revisionAssessment',
  'declined',
  'expired',
  'withdrawn',
  'awaitingRevisionReviewRequest',
  'removed'
] as const;

/** Align list/history UI when a review is closed mid-revision (round 1 reviewed). */
export const resolveEffectivePeerReviewWorkflowState = (params: {
  reviewCurrentState: string;
  reviewPreviousState?: string;
  submissionCurrentState?: string;
}): string => {
  if (
    params.submissionCurrentState === 'revision' &&
    params.reviewCurrentState === 'closed' &&
    params.reviewPreviousState
  ) {
    return params.reviewPreviousState;
  }

  return params.reviewCurrentState;
};

/**
 * Status stored on a completed revision round's invitedReviewers row (point-in-time).
 * Live workflow may already have moved on (e.g. awaiting reinvitation for round 3).
 */
export const PEER_REVIEW_ROUND_SNAPSHOT_STATUSES_ALLOWING_EDITOR_RATE = [
  'submittedFirstAssessment',
  'submittedRevisionAssessment',
  'reviewed'
] as const;

/** Workflow state used for rate-review gating (live list vs revision history). */
export const resolveWorkflowStateForRateReviewGating = (params: {
  reviewCurrentState: string;
  reviewPreviousState?: string;
  submissionCurrentState?: string;
  roundSnapshotStatus?: string;
}): string => {
  if (
    params.roundSnapshotStatus &&
    (
      PEER_REVIEW_ROUND_SNAPSHOT_STATUSES_ALLOWING_EDITOR_RATE as readonly string[]
    ).includes(params.roundSnapshotStatus)
  ) {
    return params.roundSnapshotStatus;
  }

  return resolveEffectivePeerReviewWorkflowState({
    reviewCurrentState: params.reviewCurrentState,
    reviewPreviousState: params.reviewPreviousState,
    submissionCurrentState: params.submissionCurrentState
  });
};

/** Whether the reviewer has submitted assessable feedback editors may rate. */
export const hasSubmittedPeerReviewForEditorRating = (
  caseData: PeerReviewCaseDataForRating | undefined
): boolean => {
  if (!caseData) {
    return false;
  }

  if (caseData.firstAssessment?.recommendation) {
    return true;
  }

  if (caseData.revisionAssessment?.recommendation) {
    return true;
  }

  return (
    caseData.revisionAssessmentHistory?.some(
      assessment => !!assessment?.recommendation
    ) ?? false
  );
};

/** Matches peer-review list API projection (current or last archived revision assessment). */
export const getEffectiveRevisionAssessment = (
  caseData: PeerReviewCaseDataForRating | undefined
): PeerReviewCaseData['revisionAssessment'] | undefined => {
  if (!caseData) {
    return undefined;
  }

  const revisionAssessmentHistory = caseData.revisionAssessmentHistory || [];
  const lastRevisionAssessmentHistory =
    revisionAssessmentHistory.length > 0
      ? revisionAssessmentHistory[revisionAssessmentHistory.length - 1]
      : undefined;

  return caseData.revisionAssessment ?? lastRevisionAssessmentHistory;
};

/**
 * Resolves which submission round's events to load for the review timeline.
 * Aligns with editorial review history: a submitted revision verdict wins over
 * an in-progress revision assessment when choosing the round number.
 */
export const resolveSubmissionRoundNumberForTimeline = (params: {
  firstAssessment?: PeerReviewCaseData['firstAssessment'];
  revisionAssessment?: PeerReviewCaseData['revisionAssessment'];
  submissionRevisionNumber?: number | null;
}): number | undefined => {
  const { firstAssessment, revisionAssessment, submissionRevisionNumber } =
    params;

  const hasSubmittedRevisionVerdict = !!revisionAssessment?.recommendation;
  const assessmentForRound = hasSubmittedRevisionVerdict
    ? revisionAssessment
    : firstAssessment;

  const raw =
    assessmentForRound?.submissionRoundNumber ??
    revisionAssessment?.submissionRoundNumber ??
    firstAssessment?.submissionRoundNumber;

  if (raw != null && String(raw).trim() !== '') {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  if (
    submissionRevisionNumber != null &&
    Number.isFinite(submissionRevisionNumber)
  ) {
    return submissionRevisionNumber;
  }

  return undefined;
};

/** Single source of truth for showing / accepting editor rate-review. */
export const canEditorRatePeerReview = (params: {
  caseData: PeerReviewCaseDataForRating | undefined;
  workflowCurrentState: string;
  isReviewRated?: boolean;
  isUserDeactivated?: boolean;
}): boolean => {
  if (params.isUserDeactivated) {
    return false;
  }

  if (
    params.isReviewRated ||
    isPeerReviewRated(params.caseData as PeerReviewCaseData | undefined)
  ) {
    return false;
  }

  if (!hasSubmittedPeerReviewForEditorRating(params.caseData)) {
    return false;
  }

  if (
    (
      PEER_REVIEW_WORKFLOW_STATES_BLOCKING_EDITOR_RATE as readonly string[]
    ).includes(params.workflowCurrentState)
  ) {
    return false;
  }

  return true;
};

type RevisionRoundSnapshotLookup = {
  currentRevisionHistory?: {
    rounds?: { invitedReviewers?: { reviewId: string; status?: string }[] }[];
  };
  previousRevisionHistory?: {
    rounds?: { invitedReviewers?: { reviewId: string; status?: string }[] }[];
  }[];
  activeRevisionRound?: {
    invitedReviewers?: { reviewId: string; status?: string }[];
  };
};

const isSnapshotStatusAllowingEditorRate = (status?: string): boolean =>
  !!status &&
  (
    PEER_REVIEW_ROUND_SNAPSHOT_STATUSES_ALLOWING_EDITOR_RATE as readonly string[]
  ).includes(status);

/**
 * Completed-round status for this review, if one exists.
 * The active round can already be reinvited or in progress; that status must
 * not hide an earlier round the reviewer actually finished.
 */
export const findRevisionRoundSnapshotStatusForReview = (
  submissionCaseData: RevisionRoundSnapshotLookup | undefined,
  reviewRequestId: string
): string | undefined => {
  if (!submissionCaseData) {
    return undefined;
  }

  const fromInvited = (
    invited?: { reviewId: string; status?: string }[]
  ): string | undefined =>
    invited?.find(r => r.reviewId === reviewRequestId)?.status;

  const statuses = [
    fromInvited(submissionCaseData.activeRevisionRound?.invitedReviewers),
    ...(submissionCaseData.currentRevisionHistory?.rounds ?? []).map(round =>
      fromInvited(round.invitedReviewers)
    ),
    ...(submissionCaseData.previousRevisionHistory ?? []).flatMap(history =>
      (history.rounds ?? []).map(round => fromInvited(round.invitedReviewers))
    )
  ];

  return statuses.find(isSnapshotStatusAllowingEditorRate);
};

/**
 * Rate Review on a closed revision-round row.
 * Uses only that round's snapshot. The live workflow (reinvited, in progress,
 * awaiting reinvitation) must not show the action on a round the reviewer
 * did not complete.
 */
export const canEditorRateCompletedRevisionRound = (params: {
  caseData: PeerReviewCaseDataForRating | undefined;
  roundSnapshotStatus?: string;
  roundCompleted: boolean;
  isReviewRated?: boolean;
  isUserDeactivated?: boolean;
}): boolean => {
  if (!params.roundCompleted || !params.roundSnapshotStatus) {
    return false;
  }

  if (
    !(
      PEER_REVIEW_ROUND_SNAPSHOT_STATUSES_ALLOWING_EDITOR_RATE as readonly string[]
    ).includes(params.roundSnapshotStatus)
  ) {
    return false;
  }

  return canEditorRatePeerReview({
    caseData: params.caseData,
    workflowCurrentState: params.roundSnapshotStatus,
    isReviewRated: params.isReviewRated,
    isUserDeactivated: params.isUserDeactivated
  });
};

/** Validates RateReview commands (peer-review table + revision history). */
export const canAcceptEditorRateReviewCommand = (params: {
  peerReviewCaseData: PeerReviewCaseDataForRating | undefined;
  reviewCurrentState: string;
  reviewPreviousState?: string;
  submissionCurrentState?: string;
  roundSnapshotStatus?: string;
  workflowHasRateReviewAction: boolean;
}): boolean => {
  const isRated = isPeerReviewRated(
    params.peerReviewCaseData as PeerReviewCaseData | undefined
  );

  if (isRated) {
    return false;
  }

  if (!hasSubmittedPeerReviewForEditorRating(params.peerReviewCaseData)) {
    return false;
  }

  const workflowStateForGating = resolveWorkflowStateForRateReviewGating({
    reviewCurrentState: params.reviewCurrentState,
    reviewPreviousState: params.reviewPreviousState,
    submissionCurrentState: params.submissionCurrentState,
    roundSnapshotStatus: params.roundSnapshotStatus
  });

  if (
    canEditorRatePeerReview({
      caseData: params.peerReviewCaseData,
      workflowCurrentState: workflowStateForGating,
      isReviewRated: isRated
    })
  ) {
    return true;
  }

  return params.workflowHasRateReviewAction;
};

/** Whether an editor has rated this peer review (score may live on any assessment). */
export const isPeerReviewRated = (
  caseData: PeerReviewCaseData | undefined
): boolean => {
  if (!caseData) {
    return false;
  }

  if (caseData.firstAssessment?.reviewScore) {
    return true;
  }

  if (caseData.revisionAssessment?.reviewScore) {
    return true;
  }

  return (
    caseData.revisionAssessmentHistory?.some(
      assessment => !!assessment?.reviewScore
    ) ?? false
  );
};

export const areaOfExpertiseOptionMap = {
  fullyMatched: 'fully',
  partiallyMatched: 'partly'
};

export const areaOfExpertiseOptions = Object.keys(areaOfExpertiseOptionMap);

export const booleanOptionsMap = {
  yes: 'Yes',
  no: 'No'
} as const;

export const booleanOptions = Object.keys(
  booleanOptionsMap
) as (keyof typeof booleanOptionsMap)[];

// We show a subset of peer reviews depending on being in a review round or not.
export const peerReviewStatusByReviewRound = {
  firstReviewRound: [
    'invited',
    'declined',
    'closed',
    'expired',
    'rescinded',
    'withdrawn',
    'firstAssessment',
    'submittedFirstAssessment'
  ],
  revisionReviewRound: [
    'awaitingRevision',
    'awaitingRevisionReviewRequest',
    'revisionAssessment',
    'submittedRevisionAssessment',
    'revisionInvited',
    'declined',
    'expired',
    'withdrawn',
    'rescinded'
  ],
  reviewed: ['reviewed']
};

export const REVIEW_STATUS_CURRENT_STATE_MAP: Record<
  string,
  IPeerReviewWFState[]
> = {
  inProgress: [
    'firstAssessment',
    'revisionAssessment',
    'assessment',
    'revision'
  ],
  reviewed: [
    'reviewed',
    'submittedRevisionAssessment',
    'awaitingRevision',
    'submittedFirstAssessment'
  ],
  invited: ['revisionInvited', 'invited'],
  awaitingReinvitation: ['awaitingRevisionReviewRequest'],
  declined: ['declined'],
  expired: ['expired'],
  withdrawn: ['withdrawn'],
  rescinded: ['rescinded']
};

/**
 * Active = Requires attention.
 * Inactive = Does not require attention.
 */
const activeVsInactiveReviewStatus: Record<
  IPeerReviewWFState,
  'active' | 'inactive'
> = {
  submittedFirstAssessment: 'inactive',
  awaitingRevision: 'inactive',
  awaitingRevisionReviewRequest: 'inactive',
  submittedRevisionAssessment: 'inactive',
  removed: 'inactive',
  reviewed: 'inactive',
  declined: 'inactive',
  expired: 'inactive',
  closed: 'inactive',
  withdrawn: 'inactive',
  invited: 'active',
  firstAssessment: 'active',
  rescinded: 'active',
  revisionAssessment: 'active',
  revisionInvited: 'active'
};

export const inactivePeerReviewStatuses: IPeerReviewWFState[] = Object.entries(
  activeVsInactiveReviewStatus
)
  .filter(([_, value]) => value === 'inactive')
  .map(([key, _]) => key);

export const activePeerReviewStatuses: IPeerReviewWFState[] = Object.entries(
  activeVsInactiveReviewStatus
)
  .filter(([_, value]) => value === 'active')
  .map(([key, _]) => key);

// --------------------------------------------------------------------------------
// Review Constants
// --------------------------------------------------------------------------------
export type IReviewBadge = {
  icon: any;
  variant: string;
  label: string;
  tooltipText?: string;
  reportLabel?: string;
};

// @NOTE: this is a map for all review recommendations types for all journals
export const REVIEW_RECOMMENDATION_MAP: Record<string, IReviewBadge> = {
  pending: {
    icon: faRefresh,
    variant: 'neutral',
    label: 'Pending'
  },
  awaitingReview: {
    icon: faRefresh,
    variant: 'neutral',
    label: 'Awaiting Review'
  },
  approve: {
    icon: faCheck,
    variant: 'success',
    label: 'Publish'
  },
  publish: {
    icon: faCheck,
    variant: 'success',
    label: 'Publish'
  },
  reject: {
    icon: faXmark,
    variant: 'caution',
    label: 'Reject'
  },
  minorRevision: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Minor Revision'
  },
  majorRevision: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Major Revision'
  },
  moderateRevision: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Moderate Revision'
  },
  default: {
    icon: null,
    variant: 'neutral',
    label: 'N/A'
  },
  transferOffered: {
    icon: null,
    variant: 'attention',
    label: 'Transfer Offer'
  },
  amendments: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Amendments'
  },
  accept: {
    icon: faCheck,
    variant: 'success',
    label: 'Accept'
  },
  amendmentRequired: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Amendment Required'
  },
  rejectAsAverage: {
    icon: faXmark,
    variant: 'caution',
    label: 'Reject As Average'
  },
  rejectAsErrors: {
    icon: faXmark,
    variant: 'caution',
    label: 'Reject As Errors'
  },
  unsatisfactoryRevision: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Unsatisfactory Revision'
  },
  rejectAndTransfer: {
    icon: faInfoCircle,
    variant: 'interactive',
    label: 'Reject and Transfer'
  },
  interrupted: {
    icon: faBan,
    variant: 'caution',
    label: 'Interrupted'
  },
  revision: {
    icon: faPenToSquare,
    variant: 'attention',
    label: 'Revision'
  },
  rejectWithoutReview: {
    icon: faXmark,
    variant: 'caution',
    label: 'Reject Without Review'
  },
  inviteToPeerReview: {
    icon: faInfoCircle,
    variant: 'attention',
    label: 'Invite to Peer Review'
  },
  boardMemberReview: {
    icon: faInfoCircle,
    variant: 'attention',
    label: 'Board Member Review'
  }
};
